import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronRight } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { ToolUseItem } from '../../../contracts/harness.types';
import { toolCallLabelMessages } from '../../../data/projects/timeline/toolCallLabel.messages';
import {
  summariseToolGroup,
  type ToolGroupSummary,
} from '../../../data/projects/timeline/working-line/toolGroupSummary';
import { inlineToolCallLabel } from './inlineToolCallLabel';
import { ShimmerText } from './ShimmerText';
import { toolIcon } from './toolPresentation';
import { useWorkingRowText } from './useWorkingRowText';
import { WORKING_TOGGLE_PRESS, WorkingDisclosure } from './WorkingDisclosure';

const ATOM_ANIMATE_IN =
  'motion-safe:duration-300 motion-safe:ease-out-quart motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1';

/**
 * Trailing detail for mixed error/cancelled batches only. An all-failed or
 * all-cancelled group needs no suffix — the resolver already wraps the label.
 * Running/success carry none: the title already reads cleanly.
 */
const groupCaption = (
  intl: ReturnType<typeof useIntl>,
  summary: ToolGroupSummary
): string | null => {
  const { tone, total, failed, cancelled } = summary;
  if (tone !== 'error' && tone !== 'cancelled') return null;
  const parts = [
    failed > 0 && failed < total
      ? intl.formatMessage(toolCallLabelMessages.groupFailedCount, { count: failed })
      : null,
    cancelled > 0 && cancelled < total
      ? intl.formatMessage(toolCallLabelMessages.groupCancelledCount, {
          count: cancelled,
        })
      : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
};

export interface GroupedToolUseBlockProps {
  members: ToolUseItem[];
  renderMember: (member: ToolUseItem) => ReactNode;
}

/**
 * One collapsed row for a run of ≥3 same-name tool calls — the grouped
 * sibling of `InlineToolCallRow`. The count-bearing label comes from the
 * resolver (`Edited 5 consult notes`); a chevron expands the member rows
 * beneath for tracing, flush with the row. Running batches shimmer
 * the label; a mixed error/cancelled batch appends a short localised count.
 * The nested rows carry each call's title and detail.
 */
export const GroupedToolUseBlock = ({
  members,
  renderMember,
}: GroupedToolUseBlockProps) => {
  const intl = useIntl();
  const rowText = useWorkingRowText();
  const [expanded, setExpanded] = useState(false);
  const listId = useId();

  const summary = summariseToolGroup(members);
  if (!summary) return null;

  const { tone, total, done, members: visibleMembers } = summary;
  const toolName = visibleMembers[0]?.name ?? members[0]?.name ?? '';
  const Icon = toolIcon(toolName);
  // One visible call is labelled as that call, preview included — as the Working line does.
  const [only] = visibleMembers;
  const title = inlineToolCallLabel(intl, {
    toolName,
    status: tone,
    toolCallDisplay: only?.toolCallDisplay,
    ...(total === 1 && only ? { input: only.input } : {}),
    count: total,
    done,
  });
  const caption = groupCaption(intl, summary);
  const isRunning = tone === 'running';
  // A failure keeps the ordinary grey: the words carry it, and the answer is where it is reported.
  const tintClass = 'text-text-tertiary';

  return (
    <div
      className={cn('flex w-full min-w-0 flex-col', isRunning && ATOM_ANIMATE_IN)}
      data-testid="grouped-tool-use-block"
    >
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-controls={listId}
        className={cn(
          'group/grouped flex w-full min-w-0 items-center gap-2 text-start',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
          WORKING_TOGGLE_PRESS
        )}
      >
        <Icon className={cn('size-3.5 shrink-0', tintClass)} aria-hidden />
        <TypographyP2 className={cn(rowText, tintClass)}>
          {isRunning ? <ShimmerText className="inline">{title}</ShimmerText> : title}
          {/* Same text node as the label, so the count reads at the same size
              and on the same baseline rather than as a smaller side caption. */}
          {caption ? <span>{` · ${caption}`}</span> : null}
        </TypographyP2>
        <ChevronRight
          className={cn(
            'size-3.5 shrink-0 text-text-tertiary rtl:-scale-x-100',
            'motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-out-quart motion-reduce:transition-none',
            'opacity-60 group-hover/grouped:opacity-100 group-focus-visible/grouped:opacity-100',
            expanded && 'rotate-90'
          )}
          aria-hidden
        />
      </button>

      <WorkingDisclosure id={listId} open={expanded} className="pt-2">
        <ul className="flex flex-col gap-3">
          {visibleMembers.map((member) => (
            <li key={member.id}>{renderMember(member)}</li>
          ))}
        </ul>
      </WorkingDisclosure>
    </div>
  );
};
